import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../Services/Store";
import styles from "./EmployeeDetails.module.css";
import AddEmployeePanel from "./AddEmployeePanel";
import NoDataAvailable from "../Customs/NoDataAvailable/NoDataAvailable";
import EmployeeProfile from "./EmployeeProfile";
import { Employee, AccessItem } from "../Models/Models";
import WebService from "../Services/WebService";
import Paginations from "../Customs/pagination/Paginations";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import PageLoader from "../Customs/PageLoader/PageLoader";

const PAGE_SIZE = 10;

const getInitials = (name: string): string => {
  if (!name) return "";
  const words = name.trim().split(" ");
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (
    words[0].charAt(0).toUpperCase() +
    words[words.length - 1].charAt(0).toUpperCase()
  );
};

const EmployeeDetails: React.FC = () => {
  const navigate = useNavigate();

  // --- RBAC: read/write access ---
  const accessList = useSelector(
    (state: RootState) => state.auth.access as AccessItem[] || []
  );
  const moduleAccess = accessList.find(
    (item) => item.code === "employee_management"
  );
  const canRead = moduleAccess?.read === true;
  const canWrite = moduleAccess?.write === true;

  useEffect(() => {
    if (!canRead) {
      navigate("/", { replace: true });
    }
  }, [canRead, navigate]);

  // --- State ---
  const [search, setSearch] = useState("");
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [editEmployee, setEditEmployee] = useState<Employee | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);

  // --- Fetch employees ---
  const fetchEmployees = useCallback(() => {
    setLoading(true);
    setError("");
    const searchParam = search.trim();
    let apiAction = `/v1/door-management/employees?limit=${PAGE_SIZE}&page_number=${page}`;
    if (searchParam) {
      if (/^\d+$/.test(searchParam)) {
        apiAction += `&employee_id=${encodeURIComponent(searchParam)}`;
      } else {
        apiAction += `&name=${encodeURIComponent(searchParam)}`;
      }
    }

    WebService.getAPI<{ data: { employees: Employee[]; pagination?: { total_records: number } } }>(
      {
        action: apiAction,
        type: "",
      }
    )
      .then((res) => {
        const data = res.data?.employees || [];
        setEmployees(data);
        setTotalRecords(res.data?.pagination?.total_records || data.length);
      })
      .catch((err) => {
        setError(typeof err === "string" ? err : "Failed to fetch employees");
        setEmployees([]);
        setTotalRecords(0);
      })
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // --- Open profile drawer ---
  const handleViewDetails = (emp: Employee) => {
    setSelectedEmployeeId(emp.employee_id ?? emp.user_id);
    setProfileOpen(true);
  };

  // --- Edit Employee ---
  const handleEdit = (emp: Employee) => {
    setEditEmployee(emp);
    setShowAddPanel(true);
  };

  // --- Delete Employee using SweetAlert2 ---
  const handleDelete = (emp: Employee) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Are you sure you want to delete this employee?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#FFD400",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Delete",
      customClass: {
        confirmButton: styles.confirmDeleteBtn || "",
        cancelButton: styles.cancelDeleteBtn || "",
      },
      heightAuto: false,
      focusCancel: true,
    }).then((result) => {
      if (result.isConfirmed) {
        WebService.deleteAPI({
          action: `/v1/door-management/employees/${emp.user_id}`,
        })
          .then(() => {
            fetchEmployees();
            toast.success("Employee deleted successfully!");
          })
          .catch(() => {
            toast.error("Failed to delete employee");
          });
      }
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div className={styles.searchRow}>
          <input
            type="text"
            placeholder="Search by name or employee ID..."
            className={styles.searchBar}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            style={{ textAlign: "center" }}
          />
        </div>
        {canWrite && (
          <button
            className={styles.createBtn}
            onClick={() => {
              setEditEmployee(null);
              setShowAddPanel(true);
            }}
          >
            + Create Employee
          </button>
        )}
      </div>

      {/* Employee Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ textAlign: "center" }}>Profiles</th>
              <th style={{ textAlign: "center" }}>Employee ID</th>
              <th style={{ textAlign: "center" }}>Contact number</th>
              {/* <th style={{ textAlign: "center" }}>Department</th> */}
              <th style={{ textAlign: "center" }}>Joined date</th>
              <th style={{ textAlign: "center" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", padding: "36px 0" }}>
                  <PageLoader />
                </td>
              </tr> 
            ) : error ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", padding: "36px 0" }}>
                  {error}
                </td>
              </tr>
            ) : employees.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", padding: "36px 0" }}>
                  <NoDataAvailable />
                </td>
              </tr>
            ) : (
              employees.map((info, idx) => {
                const initials = getInitials(info?.username || "");
                return (
                  <tr key={info.user_id || `row-${idx}`}>
                    <td style={{ textAlign: "center" }}>
                      <div className={styles.profileCell}>
                        <div className={styles.initialsCircle}>{initials}</div>
                        <span className={styles.name}>{info?.username || "-"}</span>
                      </div>
                    </td>
                    <td style={{ textAlign: "center" }} className={styles.empIdCol}>{info?.employee_id || "-"}</td>
                    <td style={{ textAlign: "center" }}>{info?.mobile_number || "-"}</td>
                    {/* <td style={{ textAlign: "center" }}>{info?.user_type || "-"}</td> */}
                    <td style={{ textAlign: "center" }}>
                      {info?.joining_date
                        ? new Date(info.joining_date).toLocaleDateString()
                        : "-"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <div className={styles.actionBtns}>
                        <button
                          className={styles.viewBtn}
                          onClick={() => handleViewDetails(info)}
                          title="View Details"
                        >
                          <i className="fas fa-eye" style={{ color: "#232323" }}></i>
                        </button>
                        {canWrite && (
                          <>
                            <button
                              className={styles.editBtn}
                              onClick={() => handleEdit(info)}
                              title="Edit"
                            >
                              <i className="fas fa-edit" style={{ color: "#FFD400" }}></i>
                            </button>
                            <button
                              className={styles.deleteBtn}
                              onClick={() => handleDelete(info)}
                              title="Delete"
                            >
                              <i className="fa-solid fa-trash" style={{ color: "#E43E2D" }}></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Entries note + Pagination */}
      <div className={styles.bottomRow}>
        <div className={styles.entriesInfo} style={{ textAlign: "center" }}>
          {`Showing ${(page - 1) * PAGE_SIZE + 1} to ${Math.min(
            page * PAGE_SIZE,
            totalRecords
          )} of ${totalRecords} entries`}
        </div>
        <div className={styles.paginationControlWrapper}>
          <Paginations
            totalItems={totalRecords}
            itemsCountPerPage={PAGE_SIZE}
            changePage={setPage}
            changeCurrentPage={page}
          />
        </div>
      </div>

      {/* Add Employee Panel */}
      {showAddPanel && canWrite && (
        <AddEmployeePanel
          onClose={() => setShowAddPanel(false)}
          onSuccess={() => {
            setShowAddPanel(false);
            setEditEmployee(null);
            fetchEmployees();
          }}
          editEmployee={editEmployee}
        />
      )}

      {/* Employee Profile Drawer */}
      <EmployeeProfile
        open={profileOpen}
        employeeId={selectedEmployeeId}
        onClose={() => setProfileOpen(false)}
      />
    </div>
  );
};

export default EmployeeDetails;
